import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import authService from '../services/authService';
import bookingService from '../services/bookingService';
import paymentService from '../services/paymentService';
import LoginModal from '../components/auth/LoginModal';
import ParticipantSelectionModal from '../components/ParticipantSelectionModal';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';

const styles = `
.agendamento-page {
  min-height: 100vh;
  background-color: white;
}

.form-group input[type="radio"] {
  width: auto;
  margin: 0;
}

.calendar-header {
  padding: 2rem 0 0;
  background-color: white;
}

.back-btn {
  background: none;
  border: none;
  color: #5A9C9C;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.back-btn:hover {
  color: #4A8080;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
}

.calendar-booking-section {
  padding: 2rem 0 5rem;
  background-color: white;
}

.booking-layout {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3rem;
  align-items: start;
}

.booking-left {
  padding-right: 2rem;
}

.booking-title {
  font-size: 2.5rem;
  color: #5A9C9C;
  margin-bottom: 0.5rem;
  font-weight: 300;
}

.booking-subtitle {
  color: #4A8080;
  font-size: 1.125rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.weekend-notice {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  color: #92400e;
  padding: 0.875rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.calendar-section-title {
  font-size: 1.25rem;
  color: #374151;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.timezone-info {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 2rem;
}

.calendar-container {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  background-color: white;
}

.calendar-header-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.month-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #374151;
  text-transform: capitalize;
}

.month-nav-btn {
  background: none;
  border: none;
  color: #5A9C9C;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem 0.75rem;
  transition: color 0.3s;
}

.month-nav-btn:hover {
  color: #4A8080;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.calendar-weekdays div {
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  padding: 0.5rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  font-size: 0.95rem;
  color: #374151;
  border: 1px solid transparent;
}

.calendar-day.empty {
  cursor: default;
}

.calendar-day:not(.disabled):not(.empty):hover {
  background-color: #f0f9f9;
  border-color: #5A9C9C;
}

.calendar-day.selected {
  background-color: #4A8080;
  color: white;
  border-color: #4A8080;
}

.calendar-day.disabled {
  color: #d1d5db;
  cursor: not-allowed;
  background-color: #f9fafb;
  opacity: 0.5;
}

.availability-dot {
  width: 4px;
  height: 4px;
  background-color: #5A9C9C;
  border-radius: 50%;
  position: absolute;
  bottom: 4px;
}

.calendar-day.selected .availability-dot {
  background-color: white;
}

.time-slots-container {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.availability-info {
  font-size: 1rem;
  color: #374151;
  margin-bottom: 1rem;
  font-weight: 500;
}

.time-slots-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.time-slot {
  padding: 0.875rem;
  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;
  color: #374151;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.time-slot:hover:not(:disabled) {
  border-color: #5A9C9C;
  background-color: #f0f9f9;
}

.time-slot.selected {
  border-color: #4A8080;
  background-color: #4A8080;
  color: white;
  font-weight: 600;
}

.time-slot:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.time-slot.occupied {
  border-color: #ef4444;
  background-color: #fee2e2;
  color: #991b1b;
  cursor: not-allowed;
}

.time-slot-spots {
  font-size: 0.75rem;
  font-weight: 500;
}

.time-slot-spots.low {
  color: #dc2626;
}

.time-slot-spots.available {
  color: #059669;
}

.booking-right {
  position: sticky;
  top: 2rem;
}

.service-details-card {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 2rem;
}

.service-details-title {
  font-size: 1.5rem;
  color: #374151;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.service-detail-section h4 {
  font-size: 1.125rem;
  color: #5A9C9C;
  margin-bottom: 1rem;
}

.service-detail-info {
  margin-bottom: 1.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #6b7280;
}

.detail-value {
  font-weight: 600;
  color: #374151;
}

.service-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.service-notes {
  font-size: 0.875rem;
  color: #92400e;
  background-color: #fef3c7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
  border-left: 3px solid #fbbf24;
}

.selected-participants-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: #f0f9f9;
  border: 2px solid #5A9C9C;
  border-radius: 0.5rem;
}

.selected-participants-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selected-participants-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.participant-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.participant-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.participant-badge.child {
  background-color: #dbeafe;
  color: #1e40af;
}

.participant-badge.adult {
  background-color: #f3e8ff;
  color: #6b21a8;
}

.participant-tag-remove {
  background: none;
  border: none;
  color: #dc2626;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  margin-left: auto;
}

.participant-tag-remove:hover {
  color: #991b1b;
}

.change-participants-btn {
  width: 100%;
  padding: 0.625rem;
  background-color: #5A9C9C;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.change-participants-btn:hover {
  background-color: #4A8080;
}

.booking-form {
  margin-top: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #5A9C9C;
  box-shadow: 0 0 0 3px rgba(90, 156, 156, 0.1);
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.error-message {
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  color: #991b1b;
  padding: 0.875rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.submit-booking-btn {
  width: 100%;
  padding: 1rem;
  background-color: #4A8080;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 1rem;
}

.submit-booking-btn:hover {
  background-color: #3A6666;
}

.submit-booking-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.payment-amount {
  font-weight: 500;
  color: #374151;
}

@media (max-width: 968px) {
  .booking-layout {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .booking-left {
    padding-right: 0;
  }

  .booking-right {
    position: relative;
    top: 0;
  }

  .time-slots-grid {
    grid-template-columns: 1fr;
  }
}
`;

const SolicitarPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { alertProps, showSuccess, showError } = useCustomAlert();

  const selectedServico = location.state?.selectedServico;
  console.log(selectedServico)
  const isFestaAniversario = selectedServico?.id === 2;
  const isPilatesOrDesenho = selectedServico?.detalhes?.semPagamento === true;
  const isPilates = selectedServico?.detalhes?.tipo === 'Pilates';
  const isDesenho = selectedServico?.detalhes?.tipo === 'Desenho';

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [paymentType, setPaymentType] = useState('deposit');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    numeroParticipantes: isFestaAniversario ? '1' : '',
    observacoes: ''
  });

  useEffect(() => {
    if (!selectedServico) {
      navigate('/agendamento');
    }
  }, [selectedServico, navigate]);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const user = authService.getCurrentUser();
      if (user) {
        setFormData(prev => ({
          ...prev,
          nome: user.nome || '',
          email: user.email || '',
          telefone: user.telefone || ''
        }));
      }
    }
  }, []);

  const getMonthName = (date) => {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const getAvailableHours = () => {
    if (isFestaAniversario) {
      return ['10:00', '15:00'];
    }
    // Pilates e Desenho têm apenas um horário: 18:30
    if (isPilates || isDesenho) {
      return ['18:30'];
    }
    return Array.from({ length: 8 }, (_, i) => {
      const hour = 10 + i;
      return `${hour}:00`;
    });
  };

  const isTimeSlotOccupied = (day, time) => {
    return false;
  };

  const getAvailableSpots = (day, time) => {
    if (isFestaAniversario) return null;
    return Math.floor(Math.random() * 20) + 3;
  };

  const isWeekend = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const hasAvailableSlots = (day) => {
    const hours = getAvailableHours();
    return hours.some(hour => !isTimeSlotOccupied(day, hour));
  };

  const isDateAvailable = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dayOfWeek = date.getDay();

    // Se for Pilates, só permite terças (dia 2)
    if (isPilates) {
      return dayOfWeek === 2 && hasAvailableSlots(day);
    }

    // Se for Desenho, só permite quartas (dia 3)
    if (isDesenho) {
      return dayOfWeek === 3 && hasAvailableSlots(day);
    }

    // Se for festa de aniversário, só permite fins de semana
    if (isFestaAniversario) {
      const weekend = isWeekend(day);
      const slots = hasAvailableSlots(day);
      console.log(`Dia ${day}: isWeekend=${weekend}, hasSlots=${slots}, isFestaAniversario=${isFestaAniversario}`);
      return weekend && slots;
    }

    return hasAvailableSlots(day);
  };

  const handleDateSelect = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const isPast = date < new Date().setHours(0, 0, 0, 0);

    if (isDateAvailable(day) && !isPast) {
      setSelectedDate(day);
      setSelectedTime(null);
    }
  };

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleParticipantsSelected = (participants) => {
    setSelectedParticipants(participants);
    setShowParticipantModal(false);
    setError('');

    if (pendingAction === 'submit') {
      setPendingAction(null);
      setTimeout(() => proceedWithSubmit(participants), 100);
    } else if (pendingAction === 'cart') {
      setPendingAction(null);
      setTimeout(() => proceedWithAddToCart(participants), 100);
    }
  };

  const handleRemoveParticipant = (participantId) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const handleChangeParticipants = () => {
    setShowParticipantModal(true);
  };

  const isFormValid = () => {
    if (!selectedDate || !selectedTime) return false;
    if (!formData.nome || !formData.email || !formData.telefone) return false;
    if (selectedServico.detalhes.participantesObrigatorio && !formData.numeroParticipantes) return false;
    return true;
  };

  const handleAddToCart = () => {
    setError('');

    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!isFormValid()) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (selectedParticipants.length === 0) {
      setPendingAction('cart');
      setShowParticipantModal(true);
      return;
    }

    proceedWithAddToCart(selectedParticipants);
  };

  const proceedWithAddToCart = (participants) => {
    const cartItem = {
      nome: selectedServico.detalhes.titulo,
      preco: selectedServico.detalhes.preco,
      duracao: selectedServico.detalhes.duracao,
      data: `${selectedDate}/${currentMonth.getMonth() + 1}/${currentMonth.getFullYear()}`,
      horario: selectedTime,
      imagem: selectedServico.imagem,
      participants: participants,
      formData: { ...formData }
    };

    addToCart(cartItem);
    showSuccess('Serviço adicionado ao carrinho com sucesso!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!isFormValid()) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (selectedParticipants.length === 0) {
      setPendingAction('submit');
      setShowParticipantModal(true);
      return;
    }

    proceedWithSubmit(selectedParticipants);
  };

  const proceedWithSubmit = async (participants) => {
    setIsSubmitting(true);
    setError('');

    try {
      const bookingDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        selectedDate
      );

      const [hours, minutes] = selectedTime.split(':');
      bookingDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      let totalAmount = totalPrice;

      // Determinar o tipo de serviço
      let servicoTipo = 'Brincoteca';
      if (isFestaAniversario) {
        servicoTipo = 'FestaAniversario';
      } else if (isPilates) {
        servicoTipo = 'Pilates';
      } else if (isDesenho) {
        servicoTipo = 'Desenho';
      }

      const bookingData = {
        servicoNome: selectedServico.detalhes.titulo,
        servicoTipo: servicoTipo,
        dataHora: bookingDate.toISOString(),
        duracao: selectedServico.detalhes.duracao,
        preco: isPilatesOrDesenho ? '0 €' : `${totalAmount.toFixed(2)} €`,
        observacoes: formData.observacoes || '',
        participantIds: participants.map(p => p.id),
        numeroParticipantes: selectedServico.detalhes.participantesObrigatorio
          ? (formData.numeroParticipantes || participants.length)
          : null,
        nomeResponsavel: formData.nome,
        emailResponsavel: formData.email,
        telefoneResponsavel: formData.telefone
      };

      const booking = await bookingService.createBooking(bookingData);

      // Se for Pilates ou Desenho (sem pagamento), redirecionar direto para Meus Agendamentos
      if (isPilatesOrDesenho) {
        showSuccess('Reserva criada com sucesso! Entraremos em contacto para confirmar.');
        setTimeout(() => {
          navigate('/meus-agendamentos');
        }, 2000);
        return;
      }

      // Caso contrário, prosseguir com o pagamento normal
      const amountToPay = paymentType === 'full' ? totalAmount : totalAmount / 2;

      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/pagamento?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/agendamento`;

      const paymentSession = await paymentService.createCheckoutSession(
        booking.id,
        amountToPay,
        successUrl,
        cancelUrl,
        paymentType
      );

      window.location.href = paymentSession.sessionUrl;

    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      setError(error.message || 'Erro ao processar reserva. Por favor, tente novamente.');
      setIsSubmitting(false);
    }
  };

  const totalPrice = useMemo(() => {
    if (isPilatesOrDesenho) {
      return 0;
    }
    if (isFestaAniversario) {
      const numFromForm = parseInt(formData.numeroParticipantes, 10);
      const numParticipants = !isNaN(numFromForm) && numFromForm > 0 ? numFromForm : 1;
      const precoPorParticipante = selectedServico?.detalhes?.precoPorParticipante || 18;
      return numParticipants * precoPorParticipante;
    }
    const priceString = selectedServico?.detalhes?.preco?.replace('€', '').replace(',', '.').trim();
    return parseFloat(priceString) || 0;
  }, [isFestaAniversario, isPilatesOrDesenho, selectedServico, formData.numeroParticipantes]);

  const calculateTotalPrice = () => totalPrice;

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1 });

  if (!selectedServico) return null;

  // Mensagem de aviso personalizada para cada tipo
  let availabilityNotice = null;
  if (isFestaAniversario) {
    availabilityNotice = (
      <div className="weekend-notice">
        ⚠️ Festas de aniversário estão disponíveis apenas aos fins de semana
      </div>
    );
  } else if (isPilates) {
    availabilityNotice = (
      <div className="weekend-notice">
        ℹ️ As aulas de Pilates acontecem às terças-feiras
      </div>
    );
  } else if (isDesenho) {
    availabilityNotice = (
      <div className="weekend-notice">
        ℹ️ As aulas de Desenho acontecem às quartas-feiras
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="agendamento-page">
        <section className="calendar-header">
          <div className="container">
            <button className="back-btn" onClick={() => navigate('/')}>
              ‹ Voltar
            </button>
          </div>
        </section>

        <section className="calendar-booking-section">
          <div className="container">
            <div className="booking-layout">
              <div className="booking-left">
                <h2 className="booking-title">Agende seu serviço</h2>
                <p className="booking-subtitle">
                  Escolha a data e horário ideal para {selectedServico.detalhes.titulo}
                </p>

                {availabilityNotice}

                <div className="calendar-section-title">Selecione uma data</div>
                <div className="timezone-info">Fuso horário: Hora de Lisboa (UTC+0)</div>

                <div className="calendar-container">
                  <div className="calendar-header-nav">
                    <button className="month-nav-btn" onClick={() => handleMonthChange(-1)}>
                      ‹
                    </button>
                    <div className="month-title">{getMonthName(currentMonth)}</div>
                    <button className="month-nav-btn" onClick={() => handleMonthChange(1)}>
                      ›
                    </button>
                  </div>

                  <div className="calendar-weekdays">
                    <div>Seg</div>
                    <div>Ter</div>
                    <div>Qua</div>
                    <div>Qui</div>
                    <div>Sex</div>
                    <div>Sáb</div>
                    <div>Dom</div>
                  </div>

                  <div className="calendar-grid">
                    {emptyDays.map((_, idx) => (
                      <div key={`empty-${idx}`} className="calendar-day empty"></div>
                    ))}
                    {calendarDays.map((day) => {
                      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                      const isPast = date < new Date().setHours(0, 0, 0, 0);
                      const available = isDateAvailable(day);

                      return (
                        <div
                          key={day}
                          className={`calendar-day ${selectedDate === day ? 'selected' : ''
                            } ${!available || isPast ? 'disabled' : ''}`}
                          onClick={() => !isPast && handleDateSelect(day)}
                        >
                          {day}
                          {available && !isPast && <div className="availability-dot"></div>}
                        </div>
                      );
                    })}
                  </div>

                  {selectedDate && (
                    <div className="time-slots-container">
                      <div className="availability-info">
                        Horários disponíveis para {selectedDate} de {getMonthName(currentMonth).split(' ')[0]}
                      </div>
                      <div className="time-slots-grid">
                        {getAvailableHours().map((time) => {
                          const occupied = isTimeSlotOccupied(selectedDate, time);
                          const spots = getAvailableSpots(selectedDate, time);

                          return (
                            <button
                              key={time}
                              className={`time-slot ${selectedTime === time ? 'selected' : ''
                                } ${occupied ? 'occupied' : ''}`}
                              onClick={() => !occupied && setSelectedTime(time)}
                              disabled={occupied}
                            >
                              <span>
                                {(isPilates || isDesenho) ? '18:30 - 19:30' : time}
                              </span>
                              {spots && !occupied && !isPilates && !isDesenho && (
                                <span className={`time-slot-spots ${spots < 5 ? 'low' : 'available'}`}>
                                  {spots} vagas
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="booking-right">
                <div className="service-details-card">
                  <h3 className="service-details-title">Detalhes da reserva</h3>

                  <div className="service-detail-section">
                    <div className="service-detail-info">
                      <div className="detail-row">
                        <span className="detail-label">Serviço:</span>
                        <span className="detail-value">{selectedServico.detalhes.titulo}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Duração:</span>
                        <span className="detail-value">{selectedServico.detalhes.duracao}</span>
                      </div>
                      {!isPilatesOrDesenho && (
                        <div className="detail-row">
                          <span className="detail-label">Preço:</span>
                          <span className="detail-value">
                            {isFestaAniversario
                              ? `€${totalPrice.toFixed(2)}`
                              : selectedServico.detalhes.preco
                            }
                          </span>
                        </div>
                      )}
                      {selectedDate && selectedTime && (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">Data:</span>
                            <span className="detail-value">
                              {selectedDate}/{currentMonth.getMonth() + 1}/{currentMonth.getFullYear()}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Horário:</span>
                            <span className="detail-value">{selectedTime}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {selectedParticipants.length > 0 && (
                    <div className="selected-participants-section">
                      <div className="selected-participants-title">Participantes selecionados:</div>
                      <div className="selected-participants-list">
                        {selectedParticipants.map((participant) => (
                          <div key={participant.id} className="participant-tag">
                            <span className={`participant-badge ${participant.type === 0 || participant.type === 'Child' ? 'child' : 'adult'}`}>
                              {participant.type === 0 || participant.type === 'Child' ? 'Criança' : 'Adulto'}
                            </span>
                            <span>{participant.nome}</span>
                            <button
                              className="participant-tag-remove"
                              onClick={() => handleRemoveParticipant(participant.id)}
                              title="Remover"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        className="change-participants-btn"
                        onClick={handleChangeParticipants}
                      >
                        Alterar seleção
                      </button>
                    </div>
                  )}

                  {selectedDate && selectedTime && (
                    <form className="booking-form" onSubmit={handleSubmit}>
                      {error && (
                        <div className="error-message">
                          {error}
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="nome">Nome completo *</label>
                        <input
                          type="text"
                          id="nome"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          required
                          placeholder="Digite seu nome completo"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="seu@email.com"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="telefone">Telefone *</label>
                        <input
                          type="tel"
                          id="telefone"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleChange}
                          required
                          placeholder="936 678 220"
                        />
                      </div>

                      {selectedServico.detalhes.participantesObrigatorio && (
                        <div className="form-group">
                          <label htmlFor="numeroParticipantes">Número de participantes *</label>
                          <input
                            type="number"
                            id="numeroParticipantes"
                            name="numeroParticipantes"
                            value={formData.numeroParticipantes}
                            onChange={handleChange}
                            min="1"
                            required
                            className="participant-count-input"
                            placeholder="15 crianças"
                          />
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="observacoes">Observações</label>
                        <textarea
                          id="observacoes"
                          name="observacoes"
                          value={formData.observacoes}
                          onChange={handleChange}
                          placeholder="Alguma informação adicional (alergias, necessidades especiais, etc)..."
                        />
                      </div>

                      {!isPilatesOrDesenho && (
                        <div className="form-group">
                          <label>Tipo de Pagamento *</label>
                          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="paymentType"
                                value="deposit"
                                checked={paymentType === 'deposit'}
                                onChange={(e) => setPaymentType(e.target.value)}
                              />
                              <span className="payment-amount">
                                Pagar Caução (50%) - €{(calculateTotalPrice() / 2).toFixed(2)}
                              </span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="paymentType"
                                value="full"
                                checked={paymentType === 'full'}
                                onChange={(e) => setPaymentType(e.target.value)}
                              />
                              <span className="payment-amount">
                                Pagar Total - €{calculateTotalPrice().toFixed(2)}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}

                      {isPilatesOrDesenho && (
                        <div style={{
                          padding: '1rem',
                          backgroundColor: '#f0f9f9',
                          borderRadius: '0.5rem',
                          border: '1px solid #5A9C9C',
                          marginTop: '1rem'
                        }}>
                          <p style={{ color: '#4A8080', margin: 0, fontSize: '0.95rem' }}>
                            ℹ️ Esta reserva não requer pagamento. O mesmo é realizado na hora.
                          </p>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="submit-booking-btn"
                        disabled={!isFormValid() || isSubmitting}
                      >
                        {isSubmitting ? 'A processar...' : (isPilatesOrDesenho ? 'Confirmar Reserva' : 'Prosseguir para Pagamento')}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />

        <ParticipantSelectionModal
          isOpen={showParticipantModal}
          onClose={() => setShowParticipantModal(false)}
          onConfirm={handleParticipantsSelected}
          preSelectedParticipants={selectedParticipants}
        />

        <CustomAlert {...alertProps} />
      </div>
    </>
  );
};

export default SolicitarPage;